import React, { useState, useEffect, useCallback } from 'react';
import SMSLayout from '../components/SMSLayout';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Ban, Search, Plus, Trash2, Upload, Download, RefreshCw, 
  Phone, Calendar, AlertTriangle, CheckCircle, X, Loader2,
  MessageSquare, FileSpreadsheet, ChevronLeft, ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const sourceLabels = {
  rcs_google: { label: 'RCS Google', color: 'bg-blue-500' },
  sms_stop: { label: 'SMS STOP', color: 'bg-amber-500' },
  manual: { label: 'Manuel', color: 'bg-slate-500' },
  import: { label: 'Import', color: 'bg-purple-500' },
  api: { label: 'API', color: 'bg-green-500' }
};

const SMSBlacklist = () => {
  const { token } = useAuth();
  const { isDarkMode } = useTheme();
  
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [searchPhone, setSearchPhone] = useState('');
  const [filterSource, setFilterSource] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Add modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addMode, setAddMode] = useState('single'); // 'single' or 'bulk'
  const [newPhone, setNewPhone] = useState('');
  const [newReason, setNewReason] = useState('');
  const [bulkPhones, setBulkPhones] = useState('');
  const [adding, setAdding] = useState(false);

  const fetchBlacklist = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50'
      });
      
      if (filterSource !== 'all') params.append('source', filterSource);
      if (searchPhone) params.append('phone_search', searchPhone);
      
      const response = await axios.get(`${API_URL}/v2/blacklist?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEntries(response.data.entries || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching blacklist:', error);
      toast.error('Erreur lors du chargement de la blacklist');
    } finally {
      setLoading(false);
    }
  }, [token, page, filterSource, searchPhone]);

  useEffect(() => {
    fetchBlacklist();
  }, [fetchBlacklist]);

  const handleAddSingle = async () => {
    if (!newPhone.trim()) {
      toast.error('Veuillez entrer un numéro de téléphone');
      return;
    }
    
    setAdding(true);
    try {
      await axios.post(`${API_URL}/v2/blacklist`, {
        phone: newPhone.trim(),
        reason: newReason || 'manual',
        source: 'manual'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Numéro ajouté à la blacklist');
      setShowAddModal(false);
      setNewPhone('');
      setNewReason('');
      fetchBlacklist();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
    } finally {
      setAdding(false);
    }
  };

  const handleAddBulk = async () => {
    const phones = bulkPhones.split(/[\n,;]/).map(p => p.trim()).filter(p => p);
    
    if (phones.length === 0) {
      toast.error('Veuillez entrer au moins un numéro');
      return;
    }
    
    setAdding(true);
    try {
      await axios.post(`${API_URL}/v2/blacklist/bulk`, {
        phones,
        source: 'import'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${phones.length} numéro(s) ajouté(s) à la blacklist`);
      setShowAddModal(false);
      setBulkPhones('');
      fetchBlacklist();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'import');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (phone) => {
    if (!window.confirm(`Supprimer ${phone} de la blacklist ?`)) return;
    
    try {
      await axios.delete(`${API_URL}/v2/blacklist/${encodeURIComponent(phone)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Numéro retiré de la blacklist');
      fetchBlacklist();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API_URL}/v2/blacklist/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `blacklist_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export terminé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <SMSLayout>
      <div className="space-y-6" data-testid="sms-blacklist-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Blacklist SMS
            </h1>
            <p className={`mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Gérez les numéros exclus des campagnes SMS
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={fetchBlacklist}
              disabled={loading}
              className={isDarkMode ? 'border-[hsl(220,14%,25%)] hover:bg-[hsl(220,14%,18%)]' : ''}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            <Button
              variant="outline"
              onClick={handleExport}
              className={isDarkMode ? 'border-[hsl(220,14%,25%)] hover:bg-[hsl(220,14%,18%)]' : ''}
            >
              <Download className="h-4 w-4 mr-2" />
              Exporter
            </Button>
            <Button
              onClick={() => setShowAddModal(true)}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-red-500/20' : 'bg-red-100'}`}>
                  <Ban className={`h-5 w-5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {total}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Total blacklistés
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-amber-500/20' : 'bg-amber-100'}`}>
                  <MessageSquare className={`h-5 w-5 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {stats?.sms_stop || 0}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    SMS STOP
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-slate-500/20' : 'bg-slate-100'}`}>
                  <Plus className={`h-5 w-5 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {stats?.manual || 0}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Ajouts manuels
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                  <Upload className={`h-5 w-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {stats?.import || 0}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Imports
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                  <Input
                    placeholder="Rechercher un numéro..."
                    value={searchPhone}
                    onChange={(e) => setSearchPhone(e.target.value)}
                    className={`pl-9 ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}`}
                  />
                </div>
              </div>
              
              <Select value={filterSource} onValueChange={setFilterSource}>
                <SelectTrigger className={`w-[180px] ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}`}>
                  <SelectValue placeholder="Source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes sources</SelectItem>
                  <SelectItem value="sms_stop">SMS STOP</SelectItem>
                  <SelectItem value="manual">Manuel</SelectItem>
                  <SelectItem value="import">Import</SelectItem>
                  <SelectItem value="api">API</SelectItem>
                </SelectContent>
              </Select>
              
              {(searchPhone || filterSource !== 'all') && (
                <Button
                  variant="ghost"
                  onClick={() => { setSearchPhone(''); setFilterSource('all'); }}
                  className={isDarkMode ? 'text-slate-400 hover:text-white' : ''}
                >
                  <X className="h-4 w-4 mr-2" />
                  Réinitialiser
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* List */}
        <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
          <CardHeader className="pb-3">
            <CardTitle className={`text-lg ${isDarkMode ? 'text-white' : ''}`}>
              Numéros blacklistés
              {total > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {total} entrée{total > 1 ? 's' : ''}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
              </div>
            ) : entries.length === 0 ? (
              <div className={`text-center py-12 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                <Ban className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="font-medium">Aucun numéro blacklisté</p>
                <p className="text-sm mt-1">Ajoutez des numéros à exclure de vos campagnes</p>
              </div>
            ) : (
              <div className="space-y-2">
                {entries.map((entry, index) => (
                  <div
                    key={entry.phone || index}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      isDarkMode ? 'bg-[hsl(220,14%,18%)]' : 'bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <Phone className={`h-5 w-5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
                      <div>
                        <p className={`font-mono font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {entry.phone}
                        </p>
                        <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                          Ajouté le {formatDate(entry.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={`${sourceLabels[entry.source]?.color || 'bg-slate-500'} text-white`}>
                        {sourceLabels[entry.source]?.label || entry.source}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemove(entry.phone)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className={`flex items-center justify-between mt-6 pt-4 border-t ${isDarkMode ? 'border-[hsl(220,14%,22%)]' : 'border-slate-200'}`}>
                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Page {page} sur {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={isDarkMode ? 'border-[hsl(220,14%,25%)]' : ''}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className={isDarkMode ? 'border-[hsl(220,14%,25%)]' : ''}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
            <DialogHeader>
              <DialogTitle className={isDarkMode ? 'text-white' : ''}>
                Ajouter à la blacklist
              </DialogTitle>
              <DialogDescription className={isDarkMode ? 'text-slate-400' : ''}>
                Ajoutez un ou plusieurs numéros à exclure des campagnes
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="flex gap-2">
                <Button
                  variant={addMode === 'single' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setAddMode('single')}
                  className={addMode === 'single' ? 'bg-indigo-600' : ''}
                >
                  Un seul
                </Button>
                <Button
                  variant={addMode === 'bulk' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setAddMode('bulk')}
                  className={addMode === 'bulk' ? 'bg-indigo-600' : ''}
                >
                  Import en masse
                </Button>
              </div>
              
              {addMode === 'single' ? (
                <>
                  <div className="space-y-2">
                    <Label className={isDarkMode ? 'text-slate-300' : ''}>Numéro de téléphone</Label>
                    <Input
                      value={newPhone}
                      onChange={(e) => setNewPhone(e.target.value)}
                      placeholder="+33612345678"
                      className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className={isDarkMode ? 'text-slate-300' : ''}>Raison (optionnel)</Label>
                    <Input
                      value={newReason}
                      onChange={(e) => setNewReason(e.target.value)}
                      placeholder="Ex: Demande de désinscription"
                      className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}
                    />
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  <Label className={isDarkMode ? 'text-slate-300' : ''}>Numéros (un par ligne)</Label>
                  <Textarea
                    value={bulkPhones}
                    onChange={(e) => setBulkPhones(e.target.value)}
                    placeholder="+33612345678&#10;+33698765432&#10;+262692123456"
                    rows={8}
                    className={`font-mono ${isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}`}
                  />
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowAddModal(false)}
                className={isDarkMode ? 'border-[hsl(220,14%,25%)]' : ''}
              >
                Annuler
              </Button>
              <Button
                onClick={addMode === 'single' ? handleAddSingle : handleAddBulk}
                disabled={adding}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {adding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Ajouter
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </SMSLayout>
  );
};

export default SMSBlacklist;
